import { useEffect, useState } from "react";
import { Plus, Trash2, Save, Loader2, Lock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const MODULE_LABELS = {
  employees: "الموظفون",
  roles: "الأدوار",
  forms: "النماذج",
  submissions: "الاستمارات",
  scanner: "ماسح QR",
};
const ACTION_LABELS = {
  view: "عرض",
  view_all: "عرض الكل",
  view_own: "عرض الخاصة",
  create: "إنشاء",
  update: "تعديل",
  delete: "حذف",
  print: "طباعة",
  use: "استخدام",
};

export default function RolesPage() {
  const { hasPermission } = useAuth();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [newRole, setNewRole] = useState({ name: "", description: "", permission_names: [] });

  const load = async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([api.get("/roles"), api.get("/permissions")]);
      setRoles(r.data);
      setPermissions(p.data);
      if (!selected && r.data.length) setSelected(r.data[0]);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Build module -> action -> permission map
  const moduleMap = {};
  permissions.forEach((p) => {
    moduleMap[p.module] = moduleMap[p.module] || {};
    moduleMap[p.module][p.action] = p;
  });
  const modules = Object.keys(moduleMap);
  const actions = Array.from(new Set(permissions.map((p) => p.action)));

  const togglePerm = (permName) => {
    if (!selected) return;
    if (selected.is_system && selected.name === "superadmin") return;
    const has = (selected.permission_names || []).includes(permName);
    setSelected({
      ...selected,
      permission_names: has
        ? selected.permission_names.filter((x) => x !== permName)
        : [...(selected.permission_names || []), permName],
    });
  };

  const savePerms = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.put(`/roles/${selected.id}/permissions`, {
        permission_names: selected.permission_names,
      });
      toast.success("تم حفظ الصلاحيات");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const createRole = async () => {
    if (!newRole.name.trim()) return toast.error("أدخل اسم الدور");
    try {
      const { data } = await api.post("/roles", newRole);
      toast.success("تم إنشاء الدور");
      setOpenCreate(false);
      setNewRole({ name: "", description: "", permission_names: [] });
      await load();
      setSelected(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteRole = async (r) => {
    if (!window.confirm(`حذف الدور "${r.name}"؟`)) return;
    try {
      await api.delete(`/roles/${r.id}`);
      toast.success("تم الحذف");
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const canCreate = hasPermission("roles.create");
  const canUpdate = hasPermission("roles.update");
  const canDelete = hasPermission("roles.delete");
  const isSuperRole = selected?.is_system && selected?.name === "superadmin";

  return (
    <div data-testid="roles-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">الأدوار والصلاحيات</h1>
          <p className="text-muted-foreground mt-1">تحكم دقيق في صلاحيات كل دور</p>
        </div>
        {canCreate && (
          <Button onClick={() => setOpenCreate(true)} data-testid="add-role-button" className="bg-primary hover:bg-primary/90">
            <Plus className="w-4 h-4 ms-1" /> دور جديد
          </Button>
        )}
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Roles list */}
        <div className="col-span-12 md:col-span-4 lg:col-span-3 gov-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-surface text-xs uppercase tracking-wider text-muted-foreground font-medium">
            الأدوار
          </div>
          <div className="divide-y divide-border/60">
            {loading && <div className="p-4 text-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline" /></div>}
            {roles.map((r) => (
              <div
                key={r.id}
                data-testid={`role-item-${r.name}`}
                onClick={() => setSelected(r)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setSelected(r); }}
                className={`w-full cursor-pointer text-start px-4 py-3 flex items-center justify-between transition-colors ${selected?.id === r.id ? "bg-accent" : "hover:bg-surface-hover"}`}
              >
                <div>
                  <div className="font-medium flex items-center gap-2">
                    {r.name}
                    {r.is_system && <Lock className="w-3 h-3 text-muted-foreground" />}
                  </div>
                  <div className="text-xs text-muted-foreground line-clamp-1">{r.description}</div>
                </div>
                {canDelete && !r.is_system && (
                  <button onClick={(e) => { e.stopPropagation(); deleteRole(r); }} className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Permissions matrix */}
        <div className="col-span-12 md:col-span-8 lg:col-span-9">
          {selected ? (
            <div className="gov-card overflow-hidden" data-testid="permissions-matrix">
              <div className="px-5 py-4 border-b border-border bg-surface flex items-center justify-between">
                <div>
                  <div className="font-display text-lg font-semibold flex items-center gap-2">
                    {selected.name} {selected.is_system && <Lock className="w-4 h-4 text-muted-foreground" />}
                  </div>
                  <div className="text-xs text-muted-foreground">{selected.description}</div>
                </div>
                {canUpdate && !isSuperRole && (
                  <Button onClick={savePerms} disabled={saving} data-testid="save-permissions-button" className="bg-primary hover:bg-primary/90">
                    {saving ? <Loader2 className="w-4 h-4 ms-2 animate-spin" /> : <Save className="w-4 h-4 ms-2" />}
                    حفظ
                  </Button>
                )}
                {isSuperRole && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Lock className="w-3.5 h-3.5" /> دور النظام — صلاحيات كاملة دائمة
                  </span>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-surface/50 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="text-start px-4 py-3 font-medium">الموديول</th>
                      {actions.map((a) => (
                        <th key={a} className="text-center px-3 py-3 font-medium">{ACTION_LABELS[a] || a}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {modules.map((m) => (
                      <tr key={m} className="border-b border-border/60 last:border-0">
                        <td className="px-4 py-3 font-medium">{MODULE_LABELS[m] || m}</td>
                        {actions.map((a) => {
                          const perm = moduleMap[m][a];
                          if (!perm) return <td key={a} className="px-3 py-3 text-center text-muted-foreground">—</td>;
                          const checked = (selected.permission_names || []).includes(perm.name);
                          return (
                            <td key={a} className="px-3 py-3 text-center">
                              <Checkbox
                                data-testid={`perm-${perm.name}`}
                                checked={isSuperRole || checked}
                                disabled={!canUpdate || isSuperRole}
                                onCheckedChange={() => togglePerm(perm.name)}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="gov-card p-12 text-center text-muted-foreground">اختر دوراً للعرض</div>
          )}
        </div>
      </div>

      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent dir="rtl">
          <DialogHeader>
            <DialogTitle className="font-display">دور جديد</DialogTitle>
            <DialogDescription>أنشئ دوراً جديداً ثم اضبط صلاحياته من الجدول</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>اسم الدور</Label>
              <Input data-testid="new-role-name" value={newRole.name} onChange={(e) => setNewRole({ ...newRole, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>الوصف</Label>
              <Textarea data-testid="new-role-desc" value={newRole.description} onChange={(e) => setNewRole({ ...newRole, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCreate(false)}>إلغاء</Button>
            <Button onClick={createRole} data-testid="save-new-role-button" className="bg-primary hover:bg-primary/90">إنشاء</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
